import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import { Grid, message } from 'antd';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { AgentChatActions, useAgentChat } from '../../../components/agent_chat/useAgentChat';
import {
	AgentActionOutcome,
	AgentMessageFeedback,
	AgentPageContext,
} from '../../../types/agentHarness';
import { withCurrentUser } from '../../../hooks/withCurrentUser';
import { withUserType } from '../../../components/withUserType';
import {
	cancelAgentTurn,
	createAgentConversation,
	exchangeAgentLogin,
	fetchAgentConversation,
	fetchAgentHistory,
	fetchAgentIdentity,
	fetchAgentModels,
	resolveAgentApproval,
	resolveAgentApprovals,
	sendAgentMessageFeedback,
	fetchAgentActions,
	fetchAgentAction,
	reconcileAgentAction,
	sendAgentMessage,
	setAgentConversationModel,
	setAgentThinkingLevel,
	downloadAgentFile,
	uploadAgentFile,
} from '../../../thunks/agent_harness_thunks';
import { cn } from '../../../utils/ClassNamesUtils';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { OscarRoleProvider, useOscarTheme } from '../../context/OscarRoleContext';
import {
	readStoredConversationId,
	sessionIdentityMatches,
	storeConversationId,
	storePanelOpenIntent,
	storeSessionIdentity,
} from '../../utils/OscarConversationStorage';
import { OscarChatSummary } from '../../types/AIAgentTypes';
import { AgentHistoryPage } from '../../../types/agentHarness';
import OscarConnectionState, { oscarComposerPlaceholder } from '../shared/OscarConnectionState';
import OscarComposer from '../oscar_panel/OscarComposer';
import OscarContextBar from '../oscar_panel/OscarContextBar';
import OscarPanelHeader from '../oscar_panel/OscarPanelHeader';
import {
	OSCAR_FULL_MEASURE,
	OSCAR_RAIL_DEFAULT_PERCENT,
	OSCAR_RAIL_MAX_PERCENT,
	OSCAR_RAIL_MIN_PERCENT,
} from '../../constants/AIAgentConstants';
import useOscarDraft from '../../hooks/useOscarDraft';
import useOscarInvocationSender from '../../hooks/useOscarInvocationSender';
import useMountEffect from '../../../hooks/useMountEffect';
import { isOscarAudience } from '../../hooks/useAgentChatEnabled';
import { OscarInvocation } from '../../utils/OscarInvocation';
import ResizableSplitPanel from '../../../components/common/ResizableSplitPanel';
import OscarChatList from './OscarChatList';
import OscarTranscript from './OscarTranscript';

interface OscarFullScreenPageProps {
	userInitial?: string;
	contextSummary?: React.ReactNode;
	contextToggleLabel?: string;
	className?: string;
	/** Injected by `withUserType` — `state.session.userType`. */
	userType?: string;
	/** Injected by `withCurrentUser` — `state.session.currentUser`. */
	currentUser?: { email?: string; nameGiven?: string };
	/** From `mapDispatchToProps` below; shape required by `useAgentChat`. */
	exchangeLogin?: AgentChatActions['exchangeLogin'];
	fetchModels?: AgentChatActions['fetchModels'];
	fetchIdentity?: AgentChatActions['fetchIdentity'];
	createConversation?: AgentChatActions['createConversation'];
	sendMessage?: AgentChatActions['sendMessage'];
	cancelTurn?: AgentChatActions['cancelTurn'];
	setConversationModel?: AgentChatActions['setConversationModel'];
	setThinkingLevel?: AgentChatActions['setThinkingLevel'];
	resolveApproval?: AgentChatActions['resolveApproval'];
	resolveApprovals?: AgentChatActions['resolveApprovals'];
	sendFeedback?: AgentChatActions['sendFeedback'];
	fetchActions?: AgentChatActions['fetchActions'];
	fetchAction?: AgentChatActions['fetchAction'];
	reconcileAction?: AgentChatActions['reconcileAction'];
	uploadFile?: AgentChatActions['uploadFile'];
	/** Not part of the chat hook — passed straight to the transcript's file cards. */
	downloadFile?: (url: string) => Promise<Blob>;
	fetchConversation?: AgentChatActions['fetchConversation'];
	/** Not part of the chat hook — feeds the recent-chats rail. */
	fetchHistory?: () => Promise<AgentHistoryPage>;
}

/**
 * Figma node 14998:125437 — Oscar at full width, driven by the real agent
 * harness through `useAgentChat`.
 *
 * Reached from the panel's expand action at `/buyer/agent`, or `/supplier/agent`
 * for a buyer's internal team.
 * Shrink and Close both go back; Shrink lands on the docked panel, Close on
 * the page with Oscar put away.
 *
 * The recent-chats rail lists the person's saved conversations
 * (`GET /api/history`, stored in the OpenWrench intelligence service), and
 * picking one continues it: the harness rebuilds a conversation it no longer
 * holds from that history. The rail stays hidden while there is nothing to
 * list — a harness without history configured answers 404 — because a rail
 * headed "RECENT CHATS" over nothing advertises a feature that does not exist.
 */
const OscarFullScreenPageBase = ({
	userInitial,
	contextSummary,
	contextToggleLabel,
	className,
	userType,
	currentUser,
	...actionProps
}: OscarFullScreenPageProps) => {
	const theme = useOscarTheme();
	const userEmail = nullSafeGetOrElse('email', currentUser, '');
	// The dock gets a name from `App.tsx`; this is a route, so nobody hands it
	// one and the avatar fell back to its placeholder "?" the moment you
	// expanded. Same source `App.tsx` uses for the dock.
	const initial =
		userInitial || nullSafeGetOrElse('nameGiven', currentUser, '?').charAt(0).toUpperCase();
	const history = useHistory();
	// Shared with the dock's composer, so Expand and Shrink carry it across.
	const [draft, setDraft] = useOscarDraft(userEmail);
	const [showLogs, setShowLogs] = useState(false);
	const [contextCollapsed, setContextCollapsed] = useState(true);
	// Questions the dock was still waiting to send when Expand was pressed. Their context was
	// resolved there (Share switch included), so it is sent as is.
	const [carriedInvocations, setCarriedInvocations] = useState<OscarInvocation[]>(() =>
		nullSafeGetOrElse('state.invocations', history.location, [])
	);

	const actions: AgentChatActions = useMemo(
		() => ({
			exchangeLogin: actionProps.exchangeLogin,
			fetchModels: actionProps.fetchModels,
			fetchIdentity: actionProps.fetchIdentity,
			createConversation: actionProps.createConversation,
			sendMessage: actionProps.sendMessage,
			cancelTurn: actionProps.cancelTurn,
			setConversationModel: actionProps.setConversationModel,
			setThinkingLevel: actionProps.setThinkingLevel,
			resolveApproval: actionProps.resolveApproval,
			resolveApprovals: actionProps.resolveApprovals,
			sendFeedback: actionProps.sendFeedback,
			fetchActions: actionProps.fetchActions,
			fetchAction: actionProps.fetchAction,
			reconcileAction: actionProps.reconcileAction,
			uploadFile: actionProps.uploadFile,
			fetchConversation: actionProps.fetchConversation,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const [chats, setChats] = useState<OscarChatSummary[]>([]);
	// Whether a read has answered yet, and whether this harness keeps history at
	// all (404 when it has none configured). The rail waits for the first and
	// stays away for the second.
	const [historyLoaded, setHistoryLoaded] = useState(false);
	const [historyUnavailable, setHistoryUnavailable] = useState(false);
	// A resumed conversation replays an `idle` per earlier turn; one read at a
	// time keeps that burst to a single request.
	const historyLoadingRef = useRef(false);
	const refreshHistory = () => {
		if (!actionProps.fetchHistory || historyLoadingRef.current) {
			return;
		}
		historyLoadingRef.current = true;
		actionProps
			.fetchHistory()
			.then(
				(page) => {
					setChats(
						page.items.map((item) => ({
							id: item.id,
							title: item.title || 'Conversation',
							runAt: item.updated_at,
						}))
					);
					setHistoryLoaded(true);
					// Answered, but this session's history is not kept: same as a 404.
					setHistoryUnavailable(page.stored === false);
				},
				// The rail is a shortcut, not the chat: a failed read keeps the last
				// list, and no history at all (404) keeps it hidden.
				(historyError) => {
					if (historyError && historyError.status === 404) {
						setHistoryUnavailable(true);
					}
				}
			)
			.then(() => {
				historyLoadingRef.current = false;
			});
	};
	const showSwitchError = (reason: string) => {
		message.error(reason);
	};
	// Shares the dock's conversation, so expanding carries the chat across
	// instead of starting a new one.
	// Bound to the signed-in account: the harness cookie and sessionStorage both
	// outlive an OpenWrench sign-out, so an unbound id would hand the next user
	// the previous user's conversation.
	const [initialConversationId] = useState(() => readStoredConversationId(userEmail));
	const chat = useAgentChat(actions, {
		conversationId: initialConversationId,
		onConversationId: (conversationId) => storeConversationId(userEmail, conversationId),
		// A live harness cookie is not proof of whose it is: it outlives sign-out
		// and a workspace switch. Reuse only when the harness agrees on the person
		// and our own record agrees on the workspace.
		canReuseSession: (harnessEmail, harnessRole) =>
			harnessEmail === userEmail &&
			harnessRole === userType &&
			sessionIdentityMatches(userEmail, userType),
		onSessionEstablished: () => storeSessionIdentity(userEmail, userType),
		// Not on mount: until the hook has checked whose harness session this is,
		// the cookie may still be a previous user's. `hello` comes after that check,
		// and each settled turn brings in the conversation just had.
		onStreamReady: refreshHistory,
		onTurnEnd: refreshHistory,
		// Beside the rail that was clicked, not inside the chat still open.
		onSwitchError: showSwitchError,
	});

	// Taken once: history state survives a reload, which would otherwise send them again. The
	// `fromDock` marker stays, since leaving depends on it.
	useMountEffect(() => {
		if (carriedInvocations.length) {
			history.replace(history.location.pathname, {
				fromDock: nullSafeGetOrElse('state.fromDock', history.location, false),
			});
		}
	});
	const dropSentInvocation = () => setCarriedInvocations((current) => current.slice(1));
	const carriedContext = (invocation: OscarInvocation) => invocation.context || undefined;
	useOscarInvocationSender(chat, carriedInvocations[0], dropSentInvocation, carriedContext);

	/**
	 * Leaving deliberately — Shrink or Close, not the left navigation.
	 *
	 * Only Shrink reopens the docked panel; Close puts Oscar away entirely, the
	 * way the panel's own Close does (OPE-5443). The intent is recorded here
	 * rather than when the panel expanded: set on the way in it survived someone
	 * walking off through the sidebar instead, and the dock then opened Oscar
	 * over whatever page they had picked.
	 *
	 * Going back is gated on the marker the dock pushes, not on
	 * `window.history.length` — that only proves *some* previous entry exists, so
	 * a fullscreen URL opened from another site still sent the user out of the
	 * app, which is the thing the check was added to prevent.
	 */
	const leave = (reopenPanel: boolean) => {
		if (reopenPanel) {
			storePanelOpenIntent();
		}
		if (nullSafeGetOrElse('state.fromDock', history.location, false)) {
			history.goBack();
			return;
		}
		history.push(`/${userType}/dashboards`);
	};

	const shrink = () => leave(true);
	const close = () => leave(false);
	const toggleContextCollapsed = () => setContextCollapsed(!contextCollapsed);

	const handleSubmit = (message: string) => {
		if (chat.busy || chat.status !== 'ready') {
			return;
		}
		void chat.sendMessage(message);
		setDraft('');
	};

	const screens = Grid.useBreakpoint();
	// Shut while something is in flight — booting, recovering, or a switch, which
	// the hook reports as connecting — so a pick or New chat cannot be silently
	// dropped by the one-switch-at-a-time rule. Open on `error` too: a failed
	// switch leaves no conversation, and the rail is the way back to one.
	const switchBlocked = chat.status === 'connecting' || chat.status === 'reconnecting';

	// Below lg there is no room for a rail beside the conversation, so it only
	// appears where there is. It shows with nothing in it yet too — "No earlier
	// chats yet" — because Expand and the panel's Chat history button both land
	// here, and a first-time user who pressed Chat history got a page without
	// the list it promised. Hidden only where the harness keeps no history.
	const showRail = !!screens.lg && !historyUnavailable;

	const conversationPane = (
		<>
			<OscarTranscript
				measure={OSCAR_FULL_MEASURE}
				entries={chat.entries}
				busy={chat.busy}
				turnStartedAt={chat.turnStartedAt}
				userInitial={initial}
				onResolveApproval={chat.resolveApproval}
				onResolveApprovals={chat.resolveApprovals}
				onSendFeedback={chat.sendFeedback}
				onReconcileAction={chat.reconcileAction}
				unsentMessages={chat.unsentMessages}
				// While the connection strip is showing its own Try again, do not
				// offer a second one — they run the same action.
				onDownloadFile={actionProps.downloadFile}
				onRetrySend={chat.status === 'error' ? undefined : chat.retrySend}
			/>
			{contextSummary ? (
				<OscarContextBar
					summary={contextSummary}
					toggleLabel={contextToggleLabel}
					toggleChecked={showLogs}
					onToggleChange={setShowLogs}
					collapsed={contextCollapsed}
					onToggleCollapsed={toggleContextCollapsed}
					className="border-0 border-t border-solid border-grey-4"
				/>
			) : null}
			<OscarConnectionState status={chat.status} error={chat.errorInfo} onRetry={chat.retry} />
			<OscarComposer
				value={draft}
				onChange={setDraft}
				onSubmit={handleSubmit}
				placeholder={oscarComposerPlaceholder(chat.status)}
				disabled={chat.status !== 'ready'}
				sending={chat.busy}
				onCancel={chat.stopTurn}
			/>
		</>
	);

	return (
		<div
			className={cn(
				// h-screen, not h-full: the antd Content this renders into has no
				// definite height, so h-full would collapse to 0. A fixed height also
				// keeps the transcript scrolling internally instead of growing the page.
				'flex h-screen w-full items-center border-0 border-l-2 border-solid px-xs pb-2.5 pt-0',
				theme.surface,
				theme.panelEdge,
				className
			)}
		>
			<div className="flex h-full min-w-0 flex-1 flex-col">
				<OscarPanelHeader
					framedMark
					onNewChat={chat.newConversation}
					newChatDisabled={switchBlocked}
					onShrink={shrink}
					onClose={close}
				/>
				<div
					className={cn(
						'flex min-h-0 w-full flex-1 rounded border-2 border-solid bg-white',
						theme.panelCardBorder
					)}
				>
					{showRail ? (
						// Resizable like the AI forms' document splits: the rail's titles
						// truncate at the Figma 380px, and a person reading old chats wants
						// it wider than someone reading the answer does.
						<ResizableSplitPanel
							className="min-h-0 w-full"
							defaultLeftPercent={OSCAR_RAIL_DEFAULT_PERCENT}
							minLeftPercent={OSCAR_RAIL_MIN_PERCENT}
							maxLeftPercent={OSCAR_RAIL_MAX_PERCENT}
							leftPanelClassName="min-h-0"
							// The right wrapper has no flex growth of its own; without this the
							// conversation is sized by its content. The rule divides it from
							// the rail.
							rightPanelClassName="flex min-h-0 flex-1 flex-col border-0 border-l border-solid border-grey-5"
							leftPanel={
								<OscarChatList
									chats={chats}
									activeChatId={chat.conversation ? chat.conversation.id : undefined}
									onSelectChat={chat.openConversation}
									onNewChat={chat.newConversation}
									disabled={switchBlocked}
									loaded={historyLoaded}
									className="w-full"
								/>
							}
							rightPanel={conversationPane}
						/>
					) : (
						<div className="flex min-h-0 min-w-0 flex-1 flex-col">{conversationPane}</div>
					)}
				</div>
			</div>
		</div>
	);
};

const mapDispatchToProps = (dispatch: any, ownProps: { userType: string }) => ({
	exchangeLogin: () => dispatch(exchangeAgentLogin(ownProps.userType)),
	fetchModels: () => dispatch(fetchAgentModels()),
	fetchIdentity: () => dispatch(fetchAgentIdentity()),
	fetchConversation: (conversationId: string) => dispatch(fetchAgentConversation(conversationId)),
	fetchHistory: () => dispatch(fetchAgentHistory()),
	createConversation: (model?: string, thinkingLevel?: string) =>
		dispatch(createAgentConversation(model, thinkingLevel)),
	sendMessage: (
		conversationId: string,
		messageId: string,
		text: string,
		context?: AgentPageContext
	) => dispatch(sendAgentMessage(conversationId, messageId, text, context)),
	cancelTurn: (conversationId: string) => dispatch(cancelAgentTurn(conversationId)),
	setConversationModel: (conversationId: string, model: string) =>
		dispatch(setAgentConversationModel(conversationId, model)),
	setThinkingLevel: (conversationId: string, thinkingLevel: string) =>
		dispatch(setAgentThinkingLevel(conversationId, thinkingLevel)),
	resolveApproval: (approvalId: string, approved: boolean) =>
		dispatch(resolveAgentApproval(approvalId, approved)),
	resolveApprovals: (ids: string[], approved: boolean) =>
		dispatch(resolveAgentApprovals(ids, approved)),
	sendFeedback: (conversationId: string, messageId: string, feedback: AgentMessageFeedback) =>
		dispatch(sendAgentMessageFeedback(conversationId, messageId, feedback)),
	fetchActions: () => dispatch(fetchAgentActions()),
	fetchAction: (actionId: string) => dispatch(fetchAgentAction(actionId)),
	reconcileAction: (actionId: string, outcome: AgentActionOutcome, evidence: string) =>
		dispatch(reconcileAgentAction(actionId, outcome, evidence)),
	uploadFile: (file: File) => dispatch(uploadAgentFile(file)),
	downloadFile: (url: string) => dispatch(downloadAgentFile(url, ownProps.userType)),
});

/**
 * The provider wraps the component that reads the theme, rather than being
 * rendered by it.
 *
 * `useOscarTheme()` inside `OscarFullScreenPageBase` resolved against the
 * default context — its own provider was further down its own tree — so it fell
 * through to `CURRENT_ENV_ROLE_TYPE`, which defaults to SUPPLIER. Fullscreen
 * showed cyan to a buyer whenever the build role and the session role differed.
 * This is the same trap the role context's own doc comment warns about.
 */
const OscarFullScreenPageWithRole = (props: OscarFullScreenPageProps) =>
	// The route is open to every supplier, and the dock's gate is not on this path: someone who
	// types the URL must not get a composer for an agent that will refuse them.
	isOscarAudience(props.userType, props.currentUser) ? (
		<OscarRoleProvider userType={props.userType}>
			<OscarFullScreenPageBase {...props} />
		</OscarRoleProvider>
	) : (
		<Redirect to="/" />
	);

export const OscarFullScreenPage = withUserType(
	withCurrentUser(connect(null, mapDispatchToProps)(OscarFullScreenPageWithRole))
);

export default OscarFullScreenPage;
